import type { Entity } from '../../types'
import { formatCurrency } from '../../lib/formatters'

interface EntityCardProps {
  entity: Entity
  onClick?: () => void
}

export function EntityCard({ entity, onClick }: EntityCardProps) {
  const formatChange = (amount: number) => {
    const sign = amount >= 0 ? '+' : ''
    return `${sign}${formatCurrency(amount)}`
  }

  const entityTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      holdco: 'Holding Company',
      opco: 'Operating Company',
      scorp: 'S Corporation',
      tradingco: 'Trading Entity',
      infraco: 'Infrastructure'
    }
    return types[type] || type
  }

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`View details for ${entity.displayName || entity.name}`}
      className="bg-swiss-white p-6 cursor-pointer transition-colors duration-150 hover:bg-swiss-off-white"
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick?.()
        }
      }}
    >
      {/* Entity name and type */}
      <div className="mb-5">
        <div className="text-swiss-body font-semibold mb-1">
          {entity.displayName || entity.name}
        </div>
        <div className="text-swiss-caption text-swiss-gray-500">
          {entityTypeLabel(entity.type)}
        </div>
      </div>

      {/* Balance */}
      <div className="font-swiss-mono text-swiss-h2 font-medium mb-1">
        {formatCurrency(entity.balance)}
      </div>

      {/* Change indicator (if available) */}
      {entity.change !== undefined && entity.change !== 0 && (
        <div
          className={`font-swiss-mono text-swiss-caption ${
            entity.change >= 0 ? 'text-swiss-green' : 'text-swiss-red'
          }`}
        >
          {formatChange(entity.change)}
        </div>
      )}

      {/* Transaction count */}
      {entity.transactionCount !== undefined && (
        <div className="text-swiss-caption text-swiss-gray-500 mt-4">
          {entity.transactionCount} transaction{entity.transactionCount !== 1 ? 's' : ''} today
        </div>
      )}
    </div>
  )
}
